import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ActionButton from "components/ui/ActionButton";
import StatusBadge from "components/ui/StatusBadge";
import {
  MdArrowBack,
  MdOutlinePerson,
  MdAccessTime,
  MdLocationOn,
} from "react-icons/md";
import { useLanguage } from "context/LanguageContext";
import { authService } from "services/authService";
import { userPortalService } from "services/userPortalService";
import type { LeaveRequestResponse, SessionParticipant } from "types/domain";
import FaceCheckinPanel, {
  SelfFaceCheckResponse,
} from "views/user/self-checkin/FaceCheckinPanel";

export interface SessionDetailTarget {
  id: string;
  title: string;
  start_time: string | null;
  end_time: string | null;
  location?: string | null;
  // Human-readable venue name/address, preferred over the raw `location`
  // string (which can be GPS coordinates for legacy sessions).
  venueName?: string | null;
}

interface StaffCardProps {
  label: string;
  emptyLabel: string;
  participants: SessionParticipant[];
}

function StaffPersonRow({ participant }: { participant: SessionParticipant }) {
  return (
    <div className="flex items-center gap-3">
      {participant.user?.avatar_url ? (
        <img
          src={participant.user.avatar_url}
          alt={participant.user?.full_name || ""}
          className="h-11 w-11 rounded-full object-cover"
        />
      ) : (
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-muted text-ink-muted">
          <MdOutlinePerson className="h-6 w-6" />
        </div>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-ink">
          {participant.user?.full_name || "—"}
        </p>
        {participant.user?.email && (
          <p className="truncate text-xs text-ink-muted">
            {participant.user.email}
          </p>
        )}
        {participant.user?.phone && (
          <p className="truncate text-xs text-ink-muted">
            {participant.user.phone}
          </p>
        )}
      </div>
    </div>
  );
}

function StaffCard({ label, emptyLabel, participants }: StaffCardProps) {
  return (
    <div className="rounded-xl border border-line p-4">
      <p className="text-xs font-semibold uppercase text-ink-muted">
        {label}
      </p>
      {participants.length > 0 ? (
        <div className="mt-2 flex flex-col gap-3">
          {participants.map((p) => (
            <StaffPersonRow key={p.id} participant={p} />
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-ink-subtle">
          {emptyLabel}
        </p>
      )}
    </div>
  );
}

/**
 * Read-only session detail for STUDENT: shows the QLL (class manager) and
 * HLV (trainer) assigned to a session, the student's own check-in/out
 * status, a leave-request affordance, and an inline camera check-in panel.
 * A dedicated page/route (not a popup) — "không dùng popup cho buổi học,
 * đi sang trang mới" — so it also behaves like a single mobile-app-style
 * screen per session, and never renders a roster of other students
 * (filtered client-side as defense in depth even though the backend is
 * expected to scope /sessions/{id}/participants for STUDENT callers).
 */
export default function SessionDetailPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  // Passed via navigate(path, { state }) from the session list — title/
  // time/location aren't otherwise fetchable from just the session id (no
  // GET /sessions/{id} exists for STUDENT callers yet). Falls back to a
  // bare id-only target if this page is reached without that state (e.g.
  // a bookmarked/refreshed URL), degrading gracefully by hiding the
  // schedule info card instead of crashing.
  const locationState = location.state as SessionDetailTarget | undefined;
  const session: SessionDetailTarget = locationState ?? {
    id: sessionId || "",
    title: "",
    start_time: null,
    end_time: null,
  };

  const currentUser = authService.getStoredUser();
  const [participants, setParticipants] = React.useState<SessionParticipant[]>(
    []
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Leave request ("xin nghỉ phép") state for this session.
  const [myLeaveRequest, setMyLeaveRequest] =
    React.useState<LeaveRequestResponse | null>(null);
  const [leaveLoading, setLeaveLoading] = React.useState(true);
  const [showLeaveForm, setShowLeaveForm] = React.useState(false);
  const [isEditingLeave, setIsEditingLeave] = React.useState(false);
  const [leaveReason, setLeaveReason] = React.useState("");
  const [leaveSubmitting, setLeaveSubmitting] = React.useState(false);
  const [leaveError, setLeaveError] = React.useState<string | null>(null);
  const [leaveDeleting, setLeaveDeleting] = React.useState(false);

  const loadParticipants = React.useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    userPortalService
      .getSessionParticipants(session.id)
      .then((res) => {
        if (active) setParticipants(Array.isArray(res) ? res : []);
      })
      .catch((err: Error) => {
        if (active) setError(err.message || t("sessionDetail.loadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [session.id, t]);

  const loadMyLeaveRequest = React.useCallback(() => {
    let active = true;
    setLeaveLoading(true);
    userPortalService
      .getMyLeaveRequests()
      .then((res) => {
        if (!active) return;
        const existing = (res.items || []).find(
          (item) => item.session_id === session.id
        );
        setMyLeaveRequest(existing ?? null);
      })
      .catch(() => {
        // Non-fatal — the leave-request affordance simply won't show a
        // pre-existing status, but submission is still allowed.
        if (active) setMyLeaveRequest(null);
      })
      .finally(() => {
        if (active) setLeaveLoading(false);
      });
    return () => {
      active = false;
    };
  }, [session.id]);

  React.useEffect(() => loadParticipants(), [loadParticipants]);
  React.useEffect(() => loadMyLeaveRequest(), [loadMyLeaveRequest]);

  const handleSubmitLeaveRequest = async () => {
    if (!leaveReason.trim()) return;
    setLeaveSubmitting(true);
    setLeaveError(null);
    try {
      const result =
        isEditingLeave && myLeaveRequest
          ? await userPortalService.updateLeaveRequest(
              myLeaveRequest.id,
              leaveReason.trim()
            )
          : await userPortalService.createLeaveRequest(
              session.id,
              leaveReason.trim()
            );
      setMyLeaveRequest(result);
      setShowLeaveForm(false);
      setIsEditingLeave(false);
      setLeaveReason("");
    } catch (err) {
      setLeaveError((err as Error).message || t("sessionDetail.leaveError"));
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const handleStartEditLeaveRequest = () => {
    if (!myLeaveRequest) return;
    setLeaveReason(myLeaveRequest.content || "");
    setIsEditingLeave(true);
    setShowLeaveForm(true);
    setLeaveError(null);
  };

  const handleDeleteLeaveRequest = async () => {
    if (!myLeaveRequest) return;
    setLeaveDeleting(true);
    setLeaveError(null);
    try {
      await userPortalService.deleteLeaveRequest(myLeaveRequest.id);
      setMyLeaveRequest(null);
    } catch (err) {
      setLeaveError((err as Error).message || t("sessionDetail.leaveError"));
    } finally {
      setLeaveDeleting(false);
    }
  };

  // Defense in depth: only ever display staff rows, never a student roster,
  // even if the backend hasn't rolled out its own role filter yet.
  const staffOnly = participants.filter((p) => p.role !== "student");
  const managers = staffOnly.filter((p) => p.role === "teacher");
  const trainers = staffOnly.filter((p) => p.role === "trainer");
  const ownParticipant = participants.find(
    (p) => p.role === "student" && p.user_id === currentUser?.id
  );

  const formatTime = (value: string | null) => {
    if (!value) return t("classesPage.notScheduled");
    return new Date(value).toLocaleString("vi-VN");
  };

  const ownStatusLabel = (status: string | null | undefined) => {
    switch (status) {
      case "checked_in":
        return t("attendanceHistory.statusCheckedIn");
      case "checked_out":
        return t("attendanceHistory.statusCheckedOut");
      case "absent":
        return t("attendanceHistory.statusAbsent");
      case "excused_absence":
        return t("attendanceHistory.statusExcused");
      default:
        return null;
    }
  };

  const ownStatusTone = (
    status: string | null | undefined
  ): "success" | "warning" | "danger" | "neutral" => {
    switch (status) {
      case "checked_in":
      case "checked_out":
        return "success";
      case "absent":
        return "danger";
      case "excused_absence":
        return "warning";
      default:
        return "neutral";
    }
  };

  const handleCheckinResult = (res: SelfFaceCheckResponse) => {
    if (!res.matched || !res.attendance) return;
    // Reflect the fresh check-in/out immediately without waiting on a
    // round trip, then reconcile with the server in the background.
    setParticipants((prev) =>
      prev.map((p) =>
        p.role === "student" && p.user_id === currentUser?.id
          ? {
              ...p,
              status: res.attendance!.status,
              checked_in_at: res.attendance!.checked_in_at,
              checked_out_at: res.attendance!.checked_out_at,
            }
          : p
      )
    );
    loadParticipants();
  };

  return (
    <div className="mt-5 flex flex-col gap-5 pb-10">
      <div className="flex items-center gap-2">
        <ActionButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="!p-1 text-ink-muted"
        >
          <MdArrowBack className="h-5 w-5" />
        </ActionButton>
        <h2 className="text-2xl font-bold text-ink">
          {t("sessionDetail.title")}
        </h2>
      </div>

      {session.title && (
        <div className="rounded-xl border border-line bg-surface p-4">
          <p className="text-base font-semibold text-ink">
            {session.title}
          </p>
          <div className="mt-2 flex items-start gap-2 text-sm text-ink-muted">
            <MdAccessTime className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {formatTime(session.start_time)}
              {session.end_time
                ? ` — ${new Date(session.end_time).toLocaleTimeString("vi-VN")}`
                : ""}
            </span>
          </div>
          {(session.venueName || session.location) && (
            <div className="mt-1 flex items-start gap-2 text-sm text-ink-muted">
              <MdLocationOn className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{session.venueName || session.location}</span>
            </div>
          )}
          {ownStatusLabel(ownParticipant?.status) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusBadge
                label={ownStatusLabel(ownParticipant?.status) as string}
                tone={ownStatusTone(ownParticipant?.status)}
              />
              {ownParticipant?.checked_in_at && (
                <span className="text-xs text-ink-muted">
                  {t("attendanceHistory.checkedInAt")}:{" "}
                  {new Date(ownParticipant.checked_in_at).toLocaleTimeString(
                    "vi-VN"
                  )}
                </span>
              )}
              {ownParticipant?.checked_out_at && (
                <span className="text-xs text-ink-muted">
                  {t("attendanceHistory.checkedOutAt")}:{" "}
                  {new Date(ownParticipant.checked_out_at).toLocaleTimeString(
                    "vi-VN"
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ink-muted">
          {t("sessionDetail.loading")}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <StaffCard
            label={t("sessionDetail.manager")}
            emptyLabel={t("sessionDetail.noManager")}
            participants={managers}
          />
          <StaffCard
            label={t("sessionDetail.trainer")}
            emptyLabel={t("sessionDetail.noTrainer")}
            participants={trainers}
          />
        </div>
      )}

      <div className="rounded-xl border border-line bg-surface p-4">
        <p className="text-xs font-semibold uppercase text-ink-muted">
          {t("sessionDetail.leaveRequest")}
        </p>

        {leaveLoading ? (
          <p className="mt-2 text-sm text-ink-muted">
            {t("sessionDetail.loading")}
          </p>
        ) : myLeaveRequest &&
          myLeaveRequest.status === "PENDING" &&
          !showLeaveForm ? (
          <div className="mt-2 flex flex-col gap-2">
            <StatusBadge
              label={t("sessionDetail.leavePending")}
              tone="warning"
            />
            {myLeaveRequest.content && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {myLeaveRequest.content}
              </p>
            )}
            {leaveError && (
              <p className="text-xs text-red-600 dark:text-red-300">
                {leaveError}
              </p>
            )}
            <div className="flex gap-2">
              <ActionButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={leaveDeleting}
                onClick={handleStartEditLeaveRequest}
              >
                {t("sessionDetail.editLeave")}
              </ActionButton>
              <ActionButton
                type="button"
                variant="danger"
                size="sm"
                disabled={leaveDeleting}
                onClick={handleDeleteLeaveRequest}
              >
                {leaveDeleting
                  ? t("sessionDetail.loading")
                  : t("sessionDetail.withdrawLeave")}
              </ActionButton>
            </div>
          </div>
        ) : myLeaveRequest && myLeaveRequest.status === "APPROVED" ? (
          <div className="mt-2 flex items-center gap-2">
            <StatusBadge
              label={t("sessionDetail.leaveApproved")}
              tone="success"
            />
          </div>
        ) : (
          <div className="mt-2">
            {myLeaveRequest && myLeaveRequest.status === "REJECTED" && (
              <div className="mb-2 flex items-center gap-2">
                <StatusBadge
                  label={t("sessionDetail.leaveRejected")}
                  tone="danger"
                />
              </div>
            )}

            {!showLeaveForm ? (
              <ActionButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowLeaveForm(true)}
              >
                {t("sessionDetail.requestLeave")}
              </ActionButton>
            ) : (
              <div className="flex flex-col gap-2">
                <textarea
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  placeholder={t("sessionDetail.leaveReasonPlaceholder")}
                  className="w-full rounded-lg border border-line p-2 text-sm text-ink focus:border-brand-500 focus:outline-none dark:bg-canvas"
                  rows={3}
                />
                {leaveError && (
                  <p className="text-xs text-red-600 dark:text-red-300">
                    {leaveError}
                  </p>
                )}
                <div className="flex gap-2">
                  <ActionButton
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={leaveSubmitting || !leaveReason.trim()}
                    onClick={handleSubmitLeaveRequest}
                  >
                    {isEditingLeave
                      ? t("sessionDetail.saveLeave")
                      : t("sessionDetail.submitLeave")}
                  </ActionButton>
                  <ActionButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={leaveSubmitting}
                    onClick={() => {
                      setShowLeaveForm(false);
                      setIsEditingLeave(false);
                      setLeaveReason("");
                      setLeaveError(null);
                    }}
                  >
                    {t("sessionDetail.cancelLeave")}
                  </ActionButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-line bg-surface p-4">
        <p className="mb-3 text-xs font-semibold uppercase text-ink-muted">
          {t("sessionDetail.checkin")}
        </p>
        <FaceCheckinPanel
          sessionId={session.id}
          onResult={handleCheckinResult}
        />
      </div>
    </div>
  );
}
